import {
  classifyKey,
  toFetchKey,
} from '../components/custom-filters/data-builder-selection-list-mapping';
import type {
  CustomFilter,
  CustomFilterCondition,
  DataBuilderCustomColumn,
} from '../data-builder.store';

export type RoleOrSkill = 'role' | 'skill';

const classifyField = (field: string): RoleOrSkill | null => {
  const category = classifyKey(toFetchKey(field));
  return category === 'role' || category === 'skill' ? category : null;
};

// Role wins over skill when both appear so the result is stable regardless of
// iteration order — the warning is per-field, and a single condition only ever
// targets one selection list anyway.
const firstRoleOrSkill = (fields: Iterable<string>): RoleOrSkill | null => {
  let skill: RoleOrSkill | null = null;
  for (const field of fields) {
    const category = classifyField(field);
    if (category === 'role') return 'role';
    if (category === 'skill') skill = 'skill';
  }
  return skill;
};

/**
 * Classify a set of unit-of-analysis selection-list keys as role/skill, or null
 * when none are role/skill (e.g. geography, RICS).
 */
export const selectionKeysTouchRoleOrSkill = (
  keys: string[]
): RoleOrSkill | null => firstRoleOrSkill(keys);

const conditionFields = (conditions: CustomFilterCondition[]): string[] =>
  conditions.map((condition) => condition.field);

/**
 * Classify a custom filter as touching role/skill by inspecting its condition
 * fields (selection-list ids like `role_k1500` / `skill_k150`).
 */
export const filterTouchesRoleOrSkill = (
  filter: CustomFilter
): RoleOrSkill | null => firstRoleOrSkill(conditionFields(filter.conditions));

/**
 * Classify a custom column as touching role/skill via any of its steps' case
 * filter conditions.
 */
export const customColumnTouchesRoleOrSkill = (
  column: DataBuilderCustomColumn
): RoleOrSkill | null =>
  firstRoleOrSkill(
    column.steps.flatMap((step) =>
      step.case === null ? [] : conditionFields(step.case.conditions)
    )
  );
